import { createFileRoute } from '@tanstack/react-router';
import { Apple, ChefHat, Tag } from 'lucide-react';
import { Suspense, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ViewContent } from '@/components/view-layout';
import { DatecodegeniePrintLabelModal } from '@/features/datecodegenie/components/datecodegenie-print-label-modal';
import { DatecodegenieRecipeManager } from '@/features/datecodegenie/components/datecodegenie-recipe-manager';
import { useOrganisation } from '@/features/organisations/api/organisation-queries';
import {
  useRecipe,
  useRecipeIngredients,
} from '@/features/recipes/api/recipes-queries';
import { RecipeCostForm } from '@/features/recipes/components/recipe-cost-form';
import { RecipeCostingsForm } from '@/features/recipes/components/recipe-costing-form';
import { RecipeDetailsTab } from '@/features/recipes/components/recipe-details-tab';
import { RecipeEditHeader } from '@/features/recipes/components/recipe-edit-header';
import { RecipeEditSectionTitle } from '@/features/recipes/components/recipe-edit-section-title';
import { RecipeIngredientsForm } from '@/features/recipes/components/recipe-ingredient-form';
import { RecipeLabelProviderModal } from '@/features/recipes/components/recipe-label-provider-modal';
import { RecipeNutrition } from '@/features/recipes/components/recipe-nutrition';
import { useTaxRates } from '@/features/tax/api/tax-queries';
import { useCurrencyFormatter } from '@/hooks/use-currency-formatter';

function RecipeEditContent({ recipeId }: { recipeId: string }) {
  const { tenantId } = Route.useParams();
  const { auth } = Route.useRouteContext();
  const { data: taxRates } = useTaxRates(tenantId);
  const { data: organisation } = useOrganisation(tenantId);
  const [selectedLabelProviderId, setSelectedLabelProviderId] = useState<
    string | null
  >(null);

  const currencyFormatter = useCurrencyFormatter('EUR'); // TODO: Don't hardcode this

  const [isLabelProviderModalOpen, setIsLabelProviderModalOpen] =
    useState(false);

  if (!organisation) {
    // TODO: Handle this better, we should just throw an error in the hook and never get to here
    return <div>Error: Organisation not found</div>;
  }

  const { data: recipe } = useRecipe(recipeId, tenantId);
  const { data: ingredients } = useRecipeIngredients(recipeId, tenantId);

  if (!recipe) {
    return <div>Error: Recipe not found</div>;
  }

  const isEditable = auth.hasRole(['admin', 'editor'], tenantId);

  const onLabelProviderClick = (labelProviderId: string) => {
    setIsLabelProviderModalOpen(false);
    setSelectedLabelProviderId(labelProviderId);
  };

  const closePrintLabelModal = () => {
    setSelectedLabelProviderId(null);
  };

  const getRecipe = () => {
    return {
      title: recipe.title,
      mass: `${recipe.total_mass_kg / recipe.yield} kg`,
      ingredients: ingredients
        .filter((ingredient) => !ingredient.is_header && !ingredient.is_note)
        .map((ingredient) => ingredient.title)
        .join(', '),
      price: currencyFormatter.format(recipe.price),
    };
  };

  return (
    <>
      <RecipeEditHeader
        recipe={recipe}
        isEditable={isEditable}
        onPrintLabel={() => setIsLabelProviderModalOpen(true)}
      />
      <ViewContent>
        <div className="flex h-full pt-6">
          <div
            className="flex flex-col flex-1 px-6 overflow-y-auto h-full"
            style={{ scrollbarGutter: 'stable' }}
          >
            <div className="space-y-4">
              <RecipeEditSectionTitle>Costing</RecipeEditSectionTitle>
              <RecipeCostingsForm
                recipe={recipe}
                isEditable={isEditable}
                taxRates={taxRates}
              />

              <RecipeCostForm
                recipe={recipe}
                organisationId={tenantId}
                isEditable={isEditable}
                currencyCode={organisation.currency} // TODO: Update RecipeCostForm to use context
                wastagePercentage={organisation.wastage}
              />

              <RecipeIngredientsForm
                isEditable={isEditable}
                recipeId={recipeId}
                recipeIngredients={ingredients}
                currency={organisation.currency}
              />
            </div>
          </div>
          <div className="flex flex-col flex-1">
            <div className="border-l pl-6 flex h-full">
              <Tabs defaultValue="method" className="w-full flex flex-col">
                <div className="pr-6">
                  <TabsList className="w-full border-b justify-start">
                    <TabsTrigger value="method">
                      <ChefHat className="mr-2" /> Details
                    </TabsTrigger>
                    <TabsTrigger value="nutrition">
                      <Apple className="mr-2" /> Nutrition
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent
                  value="method"
                  className="overflow-y-auto h-full pr-6 pb-6"
                >
                  <RecipeDetailsTab
                    recipe={recipe}
                    isEditable={isEditable}
                    tenantId={tenantId}
                  />
                </TabsContent>
                <TabsContent
                  value="nutrition"
                  className="overflow-y-auto h-full pr-6 pb-6"
                >
                  <RecipeNutrition
                    recipeId={recipe.id}
                    tenantId={tenantId}
                    servings={recipe.yield}
                  />
                </TabsContent>
                <TabsContent
                  value="labels"
                  className="overflow-y-auto h-full pr-6 pb-6"
                >
                  <DatecodegenieRecipeManager recipeId={recipe.id} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </ViewContent>
      <RecipeLabelProviderModal
        open={isLabelProviderModalOpen}
        onOpenChange={setIsLabelProviderModalOpen}
        onLabelProviderClick={onLabelProviderClick}
      />
      <DatecodegeniePrintLabelModal
        open={selectedLabelProviderId == 'datecodegenie'}
        onOpenChange={closePrintLabelModal}
        getRecipe={getRecipe}
      />
    </>
  );
}

function RecipeEditRouteComponent() {
  const { recipeId } = Route.useParams();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RecipeEditContent recipeId={recipeId} />
    </Suspense>
  );
}

export const Route = createFileRoute(
  '/_authenticated/$tenantId/recipes/$recipeId',
)({
  component: RecipeEditRouteComponent,
});
